<template>
  <div class="activity-combobox-wrapper" :class="`acb-size-${size}`" ref="wrapperRef">
    <!-- Trigger Button / Box -->
    <div
      class="combobox-trigger"
      :class="{
        'is-open': isOpen,
        'has-value': !!selectedActivity,
        'is-disabled': disabled,
        'is-invalid': isInvalid
      }"
      role="combobox"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      :aria-disabled="disabled"
      :tabindex="disabled ? -1 : 0"
      :title="selectedActivity ? activityTitle(selectedActivity) : ''"
      @click="toggleDropdown"
      @keydown.down.prevent="openDropdown"
      @keydown.enter.prevent="toggleDropdown"
      @keydown.space.prevent="toggleDropdown"
      @keydown.esc="closeDropdown()"
    >
      <!-- Selected State -->
      <div v-if="selectedActivity" class="d-flex align-items-center justify-content-between w-100 gap-2 overflow-hidden">
        <div class="d-flex align-items-center gap-2 min-w-0 flex-grow-1">
          <div class="selected-code-badge flex-shrink-0">
            <i class="bi bi-hammer me-1 text-teal-300"></i>
            <span>{{ codeOf(selectedActivity) }}</span>
          </div>
          <div class="selected-info min-w-0 flex-grow-1">
            <div class="selected-desc text-truncate">
              {{ labelOf(selectedActivity) || 'Activité sans description' }}
            </div>
            <div class="selected-meta" v-if="size !== 'sm' && (sectionName(selectedActivity) || selectedActivity.unit)">
              <span v-if="sectionName(selectedActivity)" class="badge-meta text-truncate">
                <i class="bi bi-folder2 me-1"></i>{{ sectionName(selectedActivity) }}
              </span>
              <span v-if="selectedActivity.unit" class="badge-meta">
                {{ selectedActivity.unit }}
              </span>
            </div>
          </div>
        </div>

        <div class="d-flex align-items-center gap-1 flex-shrink-0">
          <button
            v-if="clearable && !disabled"
            type="button"
            class="btn-combobox-clear"
            @click.stop="clearSelection"
            title="Désélectionner l'activité"
          >
            <i class="bi bi-x-circle-fill"></i>
          </button>
          <div class="combobox-arrow">
            <i class="bi bi-chevron-down" :class="{ 'rotate-180': isOpen }"></i>
          </div>
        </div>
      </div>

      <!-- Special option state (ex: « Sans activité assignée ») -->
      <div v-else-if="selectedExtra" class="d-flex align-items-center justify-content-between w-100 gap-2 overflow-hidden">
        <div class="d-flex align-items-center gap-2 min-w-0 text-muted-placeholder extra-selected">
          <i class="bi" :class="selectedExtra.icon || 'bi-asterisk'"></i>
          <span class="text-truncate">{{ selectedExtra.label }}</span>
        </div>
        <div class="combobox-arrow flex-shrink-0">
          <i class="bi bi-chevron-down" :class="{ 'rotate-180': isOpen }"></i>
        </div>
      </div>

      <!-- Empty / Placeholder State -->
      <div v-else class="d-flex align-items-center justify-content-between w-100 gap-2">
        <div class="d-flex align-items-center gap-2 text-muted-placeholder min-w-0">
          <span v-if="loading" class="spinner-border spinner-border-sm text-teal-600"></span>
          <i v-else class="bi bi-search text-teal-600 opacity-75"></i>
          <span class="text-truncate">{{ loading ? 'Chargement des activités...' : (placeholder || '-- Sélectionner une activité de chantier liée --') }}</span>
        </div>
        <div class="d-flex align-items-center gap-2 flex-shrink-0">
          <span v-if="activities.length > 0 && size !== 'sm'" class="badge-count-pill">
            {{ activities.length }} dispo{{ activities.length > 1 ? 's' : '' }}
          </span>
          <div class="combobox-arrow">
            <i class="bi bi-chevron-down" :class="{ 'rotate-180': isOpen }"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Teleported Dropdown Menu to prevent any overflow/clipping issues -->
    <Teleport :to="teleportTarget">
      <Transition name="combobox-popup">
        <div
          v-if="isOpen"
          ref="dropdownRef"
          class="combobox-dropdown-panel"
          :style="dropdownStyle"
          role="listbox"
          @click.stop
          @mousedown.stop
        >
          <!-- Search Header Bar -->
          <div class="combobox-search-header">
            <div class="search-input-group">
              <i class="bi bi-search search-icon"></i>
              <input
                ref="searchInputRef"
                v-model="searchQuery"
                type="text"
                class="form-control combobox-search-input"
                :placeholder="searchPlaceholder"
                autocomplete="off"
                @keydown.esc.prevent="closeDropdown(true)"
                @keydown.down.prevent="moveFocus(1)"
                @keydown.up.prevent="moveFocus(-1)"
                @keydown.enter.prevent="selectFirstResult"
                @keydown.tab="closeDropdown()"
              />
              <button
                v-if="searchQuery"
                type="button"
                class="btn-clear-search"
                @click="searchQuery = ''; searchInputRef?.focus()"
                title="Effacer la recherche"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            </div>

            <div class="d-flex align-items-center justify-content-between mt-2 px-1">
              <span class="search-count-label">
                <span v-if="searchQuery">
                  <strong>{{ filteredActivities.length }}</strong> résultat{{ filteredActivities.length > 1 ? 's' : '' }}
                </span>
                <span v-else>
                  <strong>{{ activities.length }}</strong> activité{{ activities.length > 1 ? 's' : '' }} au total
                </span>
              </span>
              <button
                v-if="showEmptyOption && !isEmptyValue"
                type="button"
                class="btn-link-reset"
                @click="clearSelection"
              >
                <i class="bi bi-dash-circle me-1"></i>{{ emptyShortLabel }}
              </button>
            </div>
          </div>

          <!-- Options Scroll Container -->
          <div ref="listRef" class="combobox-items-container custom-scrollbar">
            <!-- Empty choice (Aucune activité / Toutes les activités) -->
            <template v-if="showEmptyOption && !searchQuery">
              <div
                class="combobox-item none-item"
                :class="{ active: isEmptyValue }"
                @click="clearSelection"
                @mouseenter="focusedIndex = -1"
              >
                <div class="item-icon-none">
                  <i class="bi" :class="emptyIcon"></i>
                </div>
                <div class="flex-grow-1 min-w-0">
                  <div class="fw-bold text-dark item-title">{{ emptyLabel }}</div>
                  <div v-if="emptyDescription" class="item-sub text-muted">{{ emptyDescription }}</div>
                </div>
                <i v-if="isEmptyValue" class="bi bi-check-circle-fill text-teal-600 item-check"></i>
              </div>

              <div class="dropdown-divider-styled"></div>
            </template>

            <!-- Special options -->
            <template v-if="extraOptions.length && !searchQuery">
              <div
                v-for="extra in extraOptions"
                :key="'extra-' + extra.value"
                class="combobox-item none-item"
                :class="{ active: isExtraSelected(extra) }"
                @click="selectExtra(extra)"
                @mouseenter="focusedIndex = -1"
              >
                <div class="item-icon-none">
                  <i class="bi" :class="extra.icon || 'bi-asterisk'"></i>
                </div>
                <div class="flex-grow-1 min-w-0">
                  <div class="fw-bold text-dark item-title">{{ extra.label }}</div>
                  <div v-if="extra.description" class="item-sub text-muted">{{ extra.description }}</div>
                </div>
                <i v-if="isExtraSelected(extra)" class="bi bi-check-circle-fill text-teal-600 item-check"></i>
              </div>
              <div class="dropdown-divider-styled"></div>
            </template>

            <!-- Recently used activities -->
            <template v-if="usedActivities.length > 0 && !searchQuery">
              <div class="section-label"><i class="bi bi-clock-history me-1"></i>Récemment utilisées</div>
              <div
                v-for="act in usedActivities"
                :key="'used-' + act.id"
                class="combobox-item"
                :class="{ active: isSelected(act) }"
                @click="selectItem(act)"
                @mouseenter="focusedIndex = -1"
              >
                <div class="item-code-box">
                  <span class="code-pill">{{ codeOf(act) }}</span>
                </div>
                <div class="item-body flex-grow-1 min-w-0">
                  <div class="item-description">{{ labelOf(act) || codeOf(act) }}</div>
                  <div class="item-meta-tags d-flex align-items-center gap-1 flex-wrap mt-1">
                    <span class="meta-tag tag-used">
                      <i class="bi bi-arrow-repeat me-1"></i>Utilisée ×{{ usedActivityCounts[act.id] }}
                    </span>
                  </div>
                </div>
                <div class="item-action-end flex-shrink-0 ms-2">
                  <div v-if="isSelected(act)" class="selected-check-bubble"><i class="bi bi-check-lg"></i></div>
                  <i v-else class="bi bi-chevron-right arrow-hover"></i>
                </div>
              </div>
              <div class="section-label mt-2"><i class="bi bi-list-ul me-1"></i>Toutes les activités</div>
            </template>

            <!-- Filtered Activities List -->
            <template v-if="filteredActivities.length > 0">
              <div
                v-for="(act, index) in filteredActivities"
                :key="act.id ?? index"
                :data-index="index"
                class="combobox-item"
                :class="{
                  'active': isSelected(act),
                  'is-focused': focusedIndex === index
                }"
                role="option"
                :aria-selected="isSelected(act)"
                @click="selectItem(act)"
                @mouseenter="focusedIndex = index"
              >
                <!-- Code Badge -->
                <div class="item-code-box">
                  <span class="code-pill" v-html="highlightMatch(codeOf(act), searchQuery)"></span>
                </div>

                <!-- Content Description & Meta -->
                <div class="item-body flex-grow-1 min-w-0">
                  <div class="item-description" :title="labelOf(act) || codeOf(act)">
                    <span v-html="highlightMatch(labelOf(act) || codeOf(act), searchQuery)"></span>
                  </div>

                  <div class="item-meta-tags d-flex align-items-center gap-1 flex-wrap mt-1" v-if="sectionName(act) || act.unit">
                    <span v-if="sectionName(act)" class="meta-tag tag-section">
                      <i class="bi bi-folder2 me-1"></i><span v-html="highlightMatch(sectionName(act), searchQuery)"></span>
                    </span>
                    <span v-if="act.unit" class="meta-tag tag-unit">
                      <i class="bi bi-rulers me-1"></i>{{ act.unit }}
                    </span>
                  </div>
                </div>

                <!-- Selected Icon -->
                <div class="item-action-end flex-shrink-0 ms-2">
                  <div v-if="isSelected(act)" class="selected-check-bubble">
                    <i class="bi bi-check-lg"></i>
                  </div>
                  <i v-else class="bi bi-chevron-right arrow-hover"></i>
                </div>
              </div>
            </template>

            <!-- Empty Search State -->
            <div v-else-if="searchQuery" class="combobox-empty-state">
              <div class="empty-icon-wrap">
                <i class="bi bi-search"></i>
              </div>
              <div class="fw-bold text-dark mb-1">Aucune activité correspondante</div>
              <p class="small text-muted mb-2">Aucun résultat trouvé pour "{{ searchQuery }}"</p>
              <button type="button" class="btn btn-sm btn-outline-teal rounded-pill px-3 py-1 fw-bold" @click="searchQuery = ''">
                <i class="bi bi-arrow-clockwise me-1"></i> Réinitialiser le filtre
              </button>
            </div>

            <!-- Empty Project State -->
            <div v-else class="combobox-empty-state">
              <div class="empty-icon-wrap">
                <i class="bi bi-cone-striped"></i>
              </div>
              <div class="fw-bold text-dark mb-1">Aucune activité enregistrée</div>
              <p class="small text-muted mb-0">{{ noActivitiesText }}</p>
            </div>
          </div>

          <!-- Footer / Keyboard Hint -->
          <div class="combobox-footer">
            <div class="d-flex align-items-center gap-2 small text-muted">
              <span><kbd class="combobox-kbd">↑</kbd> <kbd class="combobox-kbd">↓</kbd> Naviguer</span>
              <span><kbd class="combobox-kbd">↵</kbd> Valider</span>
              <span><kbd class="combobox-kbd">Échap</kbd> Fermer</span>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  modelValue: {
    type: [Number, String, null],
    default: null
  },
  activities: {
    type: Array,
    default: () => []
  },
  placeholder: {
    type: String,
    default: ''
  },
  searchPlaceholder: {
    type: String,
    default: 'Rechercher par code (ex: 102.1), nom ou mot-clé...'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  isInvalid: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  // Option « vide » en tête de liste (Aucune activité / Toutes les activités)
  showEmptyOption: {
    type: Boolean,
    default: true
  },
  emptyLabel: {
    type: String,
    default: '-- Aucune activité spécifique --'
  },
  emptyDescription: {
    type: String,
    default: 'Événement transverse ou non lié à une tâche unitaire'
  },
  emptyIcon: {
    type: String,
    default: 'bi-dash-circle'
  },
  // Valeur émise lorsque l'option vide est choisie
  emptyValue: {
    type: [Number, String, null],
    default: null
  },
  // Bouton ✕ sur le déclencheur
  clearable: {
    type: Boolean,
    default: true
  },
  // Options hors activités : [{ value, label, description?, icon? }]
  extraOptions: {
    type: Array,
    default: () => []
  },
  // { [activityId]: nombre d'utilisations } → section « Récemment utilisées »
  usedActivityCounts: {
    type: Object,
    default: () => ({})
  },
  noActivitiesText: {
    type: String,
    default: 'Ce chantier ne possède pas encore de devis / activités configurées.'
  },
  // Champs affichés : pastille de code et libellé
  codeField: {
    type: String,
    default: 'name'
  },
  labelField: {
    type: String,
    default: 'description'
  },
  size: {
    type: String,
    default: 'md',
    validator: v => ['sm', 'md', 'lg'].includes(v)
  }
})

const emit = defineEmits(['update:modelValue', 'change', 'clear', 'open', 'close'])

const isOpen = ref(false)
const searchQuery = ref('')
const focusedIndex = ref(-1)
const searchInputRef = ref(null)
const wrapperRef = ref(null)
const dropdownRef = ref(null)
const listRef = ref(null)
const teleportTarget = ref('body')

const dropdownStyle = ref({
  position: 'fixed',
  top: '0px',
  left: '0px',
  width: '380px',
  zIndex: 2100
})

// Place le panneau au-dessus du conteneur le plus haut (modales « maison » à z-index élevé)
function computePanelZIndex(el) {
  let max = 0
  for (let node = el; node && node !== document.body; node = node.parentElement) {
    const z = parseInt(window.getComputedStyle(node).zIndex, 10)
    if (!Number.isNaN(z)) max = Math.max(max, z)
  }
  return Math.max(2100, max + 10)
}

let panelZIndex = 2100

const sameId = (a, b) => a !== null && a !== undefined && a !== '' && String(a) === String(b)

const isEmptyValue = computed(() =>
  props.modelValue === null || props.modelValue === undefined || props.modelValue === '' ||
  String(props.modelValue) === String(props.emptyValue)
)

const emptyShortLabel = computed(() => props.emptyLabel.replace(/^-+\s*|\s*-+$/g, ''))

const selectedExtra = computed(() =>
  props.extraOptions.find(opt => String(opt.value) === String(props.modelValue)) || null
)

const selectedActivity = computed(() => {
  if (isEmptyValue.value || selectedExtra.value) return null
  return props.activities.find(a => a && sameId(a.id, props.modelValue)) || null
})

const usedActivities = computed(() =>
  props.activities.filter(a => a && props.usedActivityCounts[a.id] > 0)
)

const normalize = (value) => String(value ?? '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')

function sectionName(act) {
  return act?.section?.name || act?.section_name || ''
}

function codeOf(act) {
  return act?.[props.codeField] || act?.name || ''
}

function labelOf(act) {
  const label = act?.[props.labelField]
  // Si le libellé est le champ utilisé en repli pour le code, on bascule sur la description
  return label && label !== codeOf(act) ? label : (act?.description || '')
}

function activityTitle(act) {
  return [codeOf(act), labelOf(act)].filter(Boolean).join(' — ')
}

const filteredActivities = computed(() => {
  const list = props.activities.filter(Boolean)
  const q = normalize(searchQuery.value.trim())
  if (!q) return list
  return list.filter(act =>
    [codeOf(act), labelOf(act), act.name, act.code, act.description, sectionName(act), act.unit]
      .some(field => normalize(field).includes(q))
  )
})

function isSelected(act) {
  return !selectedExtra.value && sameId(act.id, props.modelValue)
}

function isExtraSelected(extra) {
  return !!selectedExtra.value && String(extra.value) === String(selectedExtra.value.value)
}

function selectExtra(extra) {
  if (!isExtraSelected(extra)) {
    emit('update:modelValue', extra.value)
    emit('change', null)
  }
  closeDropdown(true)
}

function updateDropdownPosition() {
  if (!wrapperRef.value) return
  const rect = wrapperRef.value.getBoundingClientRect()
  const viewportHeight = window.innerHeight
  const viewportWidth = window.innerWidth
  const gutter = 12

  const spaceBelow = viewportHeight - rect.bottom
  const openUpward = spaceBelow < 360 && rect.top > spaceBelow
  const maxHeight = Math.max(240, Math.min(520, (openUpward ? rect.top : spaceBelow) - gutter * 2))

  const targetWidth = Math.min(Math.max(rect.width, 380), viewportWidth - gutter * 2)
  const left = Math.min(Math.max(gutter, rect.left), viewportWidth - targetWidth - gutter)

  dropdownStyle.value = {
    position: 'fixed',
    left: `${left}px`,
    width: `${targetWidth}px`,
    maxHeight: `${maxHeight}px`,
    zIndex: panelZIndex,
    ...(openUpward
      ? { bottom: `${viewportHeight - rect.top + 6}px`, top: 'auto' }
      : { top: `${rect.bottom + 6}px`, bottom: 'auto' }
    )
  }
}

function toggleDropdown() {
  if (props.disabled) return
  if (isOpen.value) {
    closeDropdown()
  } else {
    openDropdown()
  }
}

function openDropdown() {
  if (props.disabled || isOpen.value) return
  // Dans une modale Bootstrap (ou en plein écran), on reste dans ce conteneur pour garder le focus
  const fullscreenEl = document.fullscreenElement
  teleportTarget.value = wrapperRef.value?.closest('.modal') ||
    (fullscreenEl && fullscreenEl.contains(wrapperRef.value) ? fullscreenEl : 'body')
  panelZIndex = computePanelZIndex(wrapperRef.value)
  searchQuery.value = ''
  focusedIndex.value = filteredActivities.value.findIndex(a => isSelected(a))
  updateDropdownPosition()
  isOpen.value = true
  emit('open')
  window.addEventListener('resize', updateDropdownPosition, { passive: true })
  window.addEventListener('scroll', onScroll, { passive: true, capture: true })

  nextTick(() => {
    updateDropdownPosition()
    searchInputRef.value?.focus({ preventScroll: true })
    scrollFocusedIntoView()
  })
}

function closeDropdown(refocus = false) {
  if (!isOpen.value) return
  isOpen.value = false
  searchQuery.value = ''
  focusedIndex.value = -1
  window.removeEventListener('resize', updateDropdownPosition)
  window.removeEventListener('scroll', onScroll, true)
  emit('close')
  if (refocus === true) {
    wrapperRef.value?.querySelector('.combobox-trigger')?.focus({ preventScroll: true })
  }
}

function onScroll(event) {
  // Le défilement interne de la liste ne doit pas repositionner le panneau
  if (dropdownRef.value && dropdownRef.value.contains(event.target)) return
  updateDropdownPosition()
}

function selectItem(activity) {
  if (!isSelected(activity)) {
    emit('update:modelValue', activity.id)
    emit('change', activity)
  }
  closeDropdown(true)
}

function clearSelection() {
  if (!isEmptyValue.value) {
    emit('update:modelValue', props.emptyValue)
    emit('change', null)
    emit('clear')
  }
  closeDropdown(true)
}

function moveFocus(step) {
  const count = filteredActivities.value.length
  if (!count) return
  let next = focusedIndex.value + step
  if (next < 0) next = count - 1
  if (next >= count) next = 0
  focusedIndex.value = next
  scrollFocusedIntoView()
}

function scrollFocusedIntoView() {
  nextTick(() => {
    const el = listRef.value?.querySelector(`[data-index="${focusedIndex.value}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  })
}

function selectFirstResult() {
  const list = filteredActivities.value
  if (list.length === 0) return
  const target = focusedIndex.value >= 0 && focusedIndex.value < list.length
    ? list[focusedIndex.value]
    : (searchQuery.value || list.length === 1 ? list[0] : null)
  if (target) selectItem(target)
}

function escapeHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

// Surligne la recherche en ignorant accents et casse (texte échappé)
function highlightMatch(text, query) {
  const raw = String(text ?? '')
  const q = normalize(String(query ?? '').trim())
  if (!q) return escapeHtml(raw)
  const start = normalize(raw).indexOf(q)
  if (start === -1) return escapeHtml(raw)
  const end = start + q.length
  return `${escapeHtml(raw.slice(0, start))}<mark class="highlight-text">${escapeHtml(raw.slice(start, end))}</mark>${escapeHtml(raw.slice(end))}`
}

function handleClickOutside(event) {
  if (!isOpen.value) return
  const clickedInTrigger = wrapperRef.value && wrapperRef.value.contains(event.target)
  const clickedInDropdown = dropdownRef.value && dropdownRef.value.contains(event.target)
  if (!clickedInTrigger && !clickedInDropdown) {
    closeDropdown()
  }
}

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside)
  window.removeEventListener('resize', updateDropdownPosition)
  window.removeEventListener('scroll', onScroll, true)
})
</script>

<style scoped>
.activity-combobox-wrapper {
  position: relative;
  width: 100%;
  min-width: 0;
  container-type: inline-size;
}

.min-w-0,
.combobox-trigger > div {
  min-width: 0;
}

/* Champ étroit : on masque le compteur pour laisser la place au texte */
@container (max-width: 280px) {
  .badge-count-pill,
  .selected-meta {
    display: none;
  }
  .combobox-trigger {
    min-height: 42px;
    padding-left: 0.6rem;
    padding-right: 0.6rem;
  }
}

/* Trigger Box */
.combobox-trigger {
  width: 100%;
  min-height: 48px;
  padding: 0.55rem 0.85rem;
  background: #ffffff;
  border: 1.5px solid #cbd5e1;
  border-radius: 0.85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  outline: none;
  user-select: none;
  text-align: left;
}

.combobox-trigger:hover:not(.is-disabled) {
  border-color: #0f766e;
  background: #fafcfc;
  box-shadow: 0 4px 12px rgba(15, 118, 110, 0.08);
}

.combobox-trigger:focus-visible,
.combobox-trigger.is-open {
  border-color: #0f766e;
  box-shadow: 0 0 0 4px rgba(15, 118, 110, 0.15);
  background: #ffffff;
}

.combobox-trigger.is-invalid {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.15) !important;
}

.combobox-trigger.is-disabled {
  background: #f1f5f9;
  border-color: #e2e8f0;
  cursor: not-allowed;
  opacity: 0.65;
}

/* Sizes */
.acb-size-sm .combobox-trigger {
  min-height: 34px;
  padding: 0.25rem 0.6rem;
  border-radius: 0.6rem;
  border-width: 1px;
}
.acb-size-sm .selected-code-badge {
  font-size: 0.72rem;
  padding: 0.18rem 0.45rem;
  border-radius: 0.45rem;
}
.acb-size-sm .selected-desc,
.acb-size-sm .text-muted-placeholder {
  font-size: 0.8rem;
}

.acb-size-lg .combobox-trigger {
  min-height: 54px;
  padding: 0.6rem 1rem;
}

/* Selected Info */
.selected-code-badge {
  background: linear-gradient(135deg, #0f766e 0%, #064e3b 100%);
  color: #ffffff;
  font-weight: 800;
  font-size: 0.82rem;
  padding: 0.35rem 0.65rem;
  border-radius: 0.6rem;
  display: inline-flex;
  align-items: center;
  max-width: 45%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 5px rgba(15, 118, 110, 0.25);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.selected-info {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.selected-desc {
  font-weight: 700;
  font-size: 0.88rem;
  color: #0f172a;
  line-height: 1.25;
}

.selected-meta {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
  min-width: 0;
}

.badge-meta {
  font-size: 0.72rem;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 0.1rem 0.45rem;
  border-radius: 0.4rem;
  border: 1px solid #e2e8f0;
  max-width: 100%;
}

.text-muted-placeholder {
  font-size: 0.88rem;
  font-weight: 500;
  color: #64748b;
}

.extra-selected {
  color: #0f172a;
  font-weight: 700;
}

.text-teal-600 {
  color: #0f766e;
}

.badge-count-pill {
  font-size: 0.75rem;
  font-weight: 700;
  color: #0f766e;
  background: #ccfbf1;
  padding: 0.2rem 0.55rem;
  border-radius: 9999px;
  border: 1px solid #99f6e4;
  white-space: nowrap;
}

.combobox-arrow {
  color: #64748b;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  transition: transform 0.25s ease;
}

.rotate-180 {
  transform: rotate(180deg);
}

.btn-combobox-clear {
  border: none;
  background: transparent;
  color: #94a3b8;
  padding: 0.2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 0.95rem;
}

.btn-combobox-clear:hover {
  color: #ef4444;
  transform: scale(1.15);
}

/* Dropdown Panel */
.combobox-dropdown-panel {
  background: #ffffff;
  border: 1.5px solid #cbd5e1;
  border-radius: 1rem;
  box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.35), 0 0 0 1px rgba(15, 23, 42, 0.08) !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  text-align: left;
  color: #0f172a;
}

/* Search Header */
.combobox-search-header {
  padding: 0.75rem 0.85rem 0.65rem 0.85rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.search-input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 0.85rem;
  color: #0f766e;
  font-size: 0.9rem;
  pointer-events: none;
}

.combobox-search-input {
  padding-left: 2.3rem;
  padding-right: 2.2rem;
  height: 38px;
  font-size: 0.85rem;
  font-weight: 600;
  border-radius: 0.65rem;
  border: 1.5px solid #cbd5e1;
  background: #ffffff;
  transition: all 0.2s ease;
}

.combobox-search-input:focus {
  border-color: #0f766e;
  box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.15);
  background: #ffffff;
}

.btn-clear-search {
  position: absolute;
  right: 0.65rem;
  border: none;
  background: #e2e8f0;
  color: #64748b;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  transition: all 0.2s ease;
}

.btn-clear-search:hover {
  background: #cbd5e1;
  color: #0f172a;
}

.search-count-label {
  font-size: 0.74rem;
  color: #64748b;
}

.btn-link-reset {
  background: none;
  border: none;
  color: #0f766e;
  font-size: 0.74rem;
  font-weight: 700;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}

.btn-link-reset:hover {
  color: #064e3b;
  text-decoration: underline;
}

/* Items Scroll Container */
.combobox-items-container {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0.5rem;
  overscroll-behavior: contain;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.dropdown-divider-styled {
  height: 1px;
  background: #e2e8f0;
  margin: 0.35rem 0.25rem 0.45rem 0.25rem;
}

.section-label {
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
  padding: 0.25rem 0.5rem 0.35rem;
}

/* Single Combobox Item */
.combobox-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.75rem;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 0.25rem;
  border: 1px solid transparent;
  position: relative;
}

.combobox-item:hover,
.combobox-item.is-focused {
  background: #f0fdf4;
  border-color: #bbf7d0;
  transform: translateX(2px);
}

.combobox-item.active {
  background: #f0fdfa;
  border-color: #99f6e4;
}

.item-code-box {
  flex-shrink: 0;
  max-width: 40%;
}

.code-pill {
  background: #e6fffa;
  color: #0f766e;
  font-weight: 800;
  font-size: 0.78rem;
  padding: 0.25rem 0.55rem;
  border-radius: 0.5rem;
  border: 1px solid #99f6e4;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

.combobox-item.active .code-pill {
  background: #0f766e;
  color: #ffffff;
  border-color: #0f766e;
}

.item-body {
  line-height: 1.3;
}

.item-description {
  font-weight: 600;
  font-size: 0.85rem;
  color: #0f172a;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.combobox-item.active .item-description {
  color: #0f766e;
  font-weight: 700;
}

.meta-tag {
  font-size: 0.7rem;
  font-weight: 600;
  border-radius: 0.35rem;
  padding: 0.1rem 0.4rem;
}

.tag-section {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.tag-unit {
  background: #ecfeff;
  color: #0891b2;
  border: 1px solid #cffafe;
}

.tag-used {
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #dbeafe;
}

.item-action-end {
  display: flex;
  align-items: center;
}

.selected-check-bubble {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #0f766e;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 800;
  box-shadow: 0 2px 6px rgba(15, 118, 110, 0.35);
}

.arrow-hover {
  font-size: 0.8rem;
  color: #cbd5e1;
  transition: all 0.2s ease;
}

.combobox-item:hover .arrow-hover {
  color: #0f766e;
  transform: translateX(2px);
}

/* None Item */
.item-icon-none {
  width: 32px;
  height: 32px;
  border-radius: 0.6rem;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
}

.none-item .item-title {
  font-size: 0.85rem;
}

.none-item .item-sub {
  font-size: 0.72rem;
}

.item-check {
  color: #0f766e;
}

/* Empty State */
.combobox-empty-state {
  text-align: center;
  padding: 2rem 1rem;
}

.empty-icon-wrap {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  margin: 0 auto 0.75rem auto;
}

.btn-outline-teal {
  color: #0f766e;
  border-color: #0f766e;
}

.btn-outline-teal:hover {
  background: #0f766e;
  color: #ffffff;
}

/* Footer */
.combobox-footer {
  padding: 0.5rem 0.85rem;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

.combobox-kbd {
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 0.25rem;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
  font-size: 0.68rem;
  padding: 0.1rem 0.35rem;
  color: #475569;
  font-family: inherit;
  font-weight: 700;
}

.combobox-dropdown-panel :deep(.highlight-text) {
  background: #fef08a;
  color: #854d0e;
  padding: 0 0.15rem;
  border-radius: 0.2rem;
  font-weight: 700;
}

@media (max-width: 480px) {
  .combobox-footer {
    display: none;
  }
}

/* Pop-up Transition */
.combobox-popup-enter-active,
.combobox-popup-leave-active {
  transition: opacity 0.2s cubic-bezier(0.16, 1, 0.3, 1), transform 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.combobox-popup-enter-from,
.combobox-popup-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
}
</style>
